/**
 * PostIdeas.jsx
 * The posts Copilot proposes, before any of them have words.
 *
 * An idea can be removed here, and removing one matters: the order of this
 * list is what decides which slot each idea lands in, so the plan redraws
 * around it. That is also why there is no per-idea "regenerate" — the honest
 * operation is on the list, not on one row of it.
 *
 * Dependencies: components/Icons.jsx
 */

import { useState } from 'react';
import { Bulb, Close, Sparkle, Warning } from '../Icons.jsx';
import { PLATFORMS } from '../dashboard/helpers.js';

export default function PostIdeas({
  strategy,
  formats = {},
  plan = [],
  posts = [],
  onRemove,
  compact = false,
  navigate,
}) {
  const [removing, setRemoving] = useState(null);
  const ideas = strategy?.ideas ?? [];

  // Which ideas actually found a slot this month. The rest are real proposals
  // that simply did not fit, and saying so beats showing twenty-six rows and
  // generating twenty-one.
  const placed = new Set(plan.map((p) => p.title));
  const written = new Set(posts.map((p) => p.notes));

  const shown = compact ? ideas.slice(0, 5) : ideas;

  async function remove(index) {
    setRemoving(index);
    try {
      await onRemove(ideas.filter((_, i) => i !== index));
    } finally {
      setRemoving(null);
    }
  }

  return (
    <section className="card" id="ideas">
      <div className="card__head">
        <span className="tile">
          <Bulb size={21} />
        </span>
        <div className="card__head-text">
          <h2>Post ideas</h2>
          <p>
            {ideas.length === 0
              ? 'Generate a strategy and the ideas appear here.'
              : `${ideas.length} proposed, ${placed.size} placed this month.`}
          </p>
        </div>
      </div>

      {ideas.length === 0 ? (
        <div className="empty empty--inline">
          <div className="empty__art">
            <Sparkle size={26} />
          </div>
          <h3>No ideas yet</h3>
          <p>Copilot proposes the month's posts when it writes the strategy.</p>
        </div>
      ) : (
        <>
          <ul className="idea-list">
            {shown.map((idea, i) => {
              const isPlaced = placed.has(idea.title);
              const isWritten = written.has(idea.title);

              return (
                <li className="idea" key={`${idea.title}-${i}`}>
                  <span className={`idea__pillar idea__pillar--${(i % 5) + 1}`} aria-hidden="true" />

                  <div className="idea__text">
                    <div className="idea__title">{idea.title}</div>
                    {idea.summary && <div className="idea__summary">{idea.summary}</div>}

                    <div className="idea__meta">
                      {idea.pillar && <span className="tag">{idea.pillar}</span>}
                      <span className="tag tag--format">{formats[idea.format] ?? idea.format}</span>
                      {(idea.platforms ?? []).map((p) => {
                        const meta = PLATFORMS[p];
                        if (!meta) return null;
                        const { Icon: Mark, label, className } = meta;
                        return (
                          <span className={`platform-mark ${className}`} key={p} title={label}>
                            <Mark size={12} />
                            <span className="sr-only">{label}</span>
                          </span>
                        );
                      })}
                      {!isPlaced && (
                        <span className="tag tag--muted" title="There was no slot left for it">
                          Not placed
                        </span>
                      )}
                    </div>
                  </div>

                  {isWritten ? (
                    <span className="badge badge--ok">
                      <span className="dot" /> Drafted
                    </span>
                  ) : (
                    <button
                      className="idea__remove"
                      type="button"
                      onClick={() => remove(i)}
                      disabled={removing === i}
                      title="Remove this idea. The month redraws around it."
                      aria-label={`Remove "${idea.title}"`}
                    >
                      <Close size={15} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          {compact && ideas.length > shown.length && (
            <button className="link-more" onClick={() => navigate()}>
              View all {ideas.length} ideas
            </button>
          )}

          {strategy?.generatedAt && (
            <div className="notice notice--info">
              <Warning />
              <span>
                This month has already been written into drafts. Removing an idea now changes the
                plan, not the drafts — edit or delete those on the Drafts screen.
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
